import { useEffect, useState } from "react";
import { Server, Database, CheckCircle2, XCircle, Cpu, Clock, Webhook, Activity } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import api from "@/lib/api";

function Dot({ ok }) { return <span className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-300"}`} />; }

export default function Infrastructure() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/infra/health").then(({ data }) => setData(data)); }, []);
  if (!data) return <Loading />;
  const { api: apiHealth, database, integrations, counts, jobs, webhooks, recent_events } = data;
  const connected = integrations.filter((i) => i.connected).length;

  return (
    <div className="animate-fade-up space-y-6" data-testid="infrastructure-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Server size={22} className="text-[#b0862f]" /> Infrastructure</h1>
        <p className="mt-1 text-[13px] text-slate-500">Live health of your services, integrations, background jobs and webhooks. Status reflects real connection state.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Card className="p-4"><div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">API</div><div className="mt-1 flex items-center gap-1.5 font-head text-[15px] font-bold text-emerald-600"><CheckCircle2 size={16} /> Healthy</div><div className="font-mono text-[10px] text-slate-400">env: {apiHealth.environment}</div></Card>
        <Card className="p-4"><div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Database</div><div className={`mt-1 flex items-center gap-1.5 font-head text-[15px] font-bold ${database.connected ? "text-emerald-600" : "text-red-600"}`}>{database.connected ? <CheckCircle2 size={16} /> : <XCircle size={16} />} {database.connected ? "Connected" : "Down"}</div></Card>
        <Stat label="Integrations connected" value={`${connected}/${integrations.length}`} tone="blue" />
        <Stat label="Pending approvals" value={counts.approvals_pending} tone="red" />
      </div>

      <Card className="p-5" data-testid="infra-integrations">
        <div className="mb-3 flex items-center gap-2"><Cpu size={15} className="text-[#1a5fd0]" /><span className="font-head text-[14px] font-bold text-slate-900">Connected services</span></div>
        <div className="grid gap-2 sm:grid-cols-2">
          {integrations.map((i) => (
            <div key={i.name} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5" data-testid="integration-row">
              <div className="flex items-center gap-2.5"><Dot ok={i.connected} /><div><div className="text-[13px] font-semibold text-slate-800">{i.name}</div><div className="font-mono text-[10px] text-slate-400">{i.kind} · {i.detail}</div></div></div>
              <span className={`font-mono text-[9px] font-bold uppercase tracking-wider ${i.connected ? "text-emerald-600" : "text-slate-400"}`}>{i.connected ? "Connected" : "Not connected"}</span>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5" data-testid="infra-jobs">
          <div className="mb-3 flex items-center gap-2"><Clock size={15} className="text-[#b0862f]" /><span className="font-head text-[14px] font-bold text-slate-900">Background jobs</span></div>
          <div className="space-y-2">
            {jobs.map((j) => (
              <div key={j.name} className="flex items-center justify-between text-[13px]"><div><div className="font-semibold text-slate-800">{j.name}</div><div className="font-mono text-[10px] text-slate-400">{j.desc} · {j.schedule}</div></div><span className="font-mono text-[10px] text-slate-500">{j.last_run ? `ran ${String(j.last_run).slice(0, 10)}` : "no runs yet"}</span></div>
            ))}
          </div>
        </Card>
        <Card className="p-5" data-testid="infra-webhooks">
          <div className="mb-3 flex items-center gap-2"><Webhook size={15} className="text-[#1a5fd0]" /><span className="font-head text-[14px] font-bold text-slate-900">Webhooks</span></div>
          <div className="space-y-2">
            {webhooks.map((w, i) => (
              <div key={i} className="flex items-center justify-between text-[12px]"><span className="truncate font-mono text-slate-600">{w.endpoint}</span><span className={`ml-2 shrink-0 font-mono text-[9px] font-bold uppercase ${w.secured ? "text-emerald-600" : "text-amber-600"}`}>{w.secured ? "secured" : "open"}</span></div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5" data-testid="infra-events">
        <div className="mb-3 flex items-center gap-2"><Activity size={15} className="text-slate-400" /><span className="font-head text-[14px] font-bold text-slate-900">Recent system events</span></div>
        {recent_events.length === 0 ? <div className="py-4 text-center text-[13px] text-slate-400">No events yet.</div> : (
          <div className="space-y-1.5">{recent_events.map((e, i) => (<div key={i} className="flex items-center justify-between text-[12px]"><span className="text-slate-700"><b>{e.actor}</b> · {e.action}</span><span className="font-mono text-[10px] text-slate-400">{String(e.when).slice(0, 16).replace("T", " ")}</span></div>))}</div>
        )}
      </Card>
    </div>
  );
}
